import { supabase } from '@/lib/supabaseClient';
import {
  ProductionCycleRecord,
  CycleStatus,
  OperationType,
  GrowerRecord,
  GrowerContractRecord,
  ProductionUnitRecord,
  PigArrivalRecord,
  PigTransferRecord,
  MortalityEventRecord,
  ReactiveHeadcountSummary,
} from '@/types/database';

export interface CycleFilters {
  farmId?: string | null;
  status?: string;
  search?: string;
}

export interface CreateCyclePayload {
  cycle_code: string;
  batch_code: string;
  grower_id?: string | null;
  farm_id?: string | null;
  production_unit_id?: string | null;
  contract_id?: string | null;
  operation_type: OperationType;
  start_date: string;
  expected_sale_date?: string | null;
  notes?: string | null;
}

/**
 * Pure calculation helper for reactive headcount.
 * Derived strictly from non-void activity records:
 * Received + Transfers In - Mortality - Transfers Out - Sold = Expected Current Headcount
 * Preserves historical discrepancies rather than artificially balancing them.
 */
export function computeReactiveHeadcount(
  cycleId: string,
  arrivals: Array<{ head_count: number; is_void: boolean }>,
  transfersIn: Array<{ head_count: number; destination_cycle_id?: string | null; is_void: boolean }>,
  mortalities: Array<{ head_count: number; is_void: boolean }>,
  transfersOut: Array<{ head_count: number; source_cycle_id?: string | null; is_void: boolean }>,
  sales: Array<{ head_count?: number; pigs_sold_count?: number; is_void: boolean }>
): ReactiveHeadcountSummary {
  const pigsReceived = arrivals
    .filter((a) => !a.is_void)
    .reduce((sum, a) => sum + (Number(a.head_count) || 0), 0);

  const transfersInCount = transfersIn
    .filter((t) => !t.is_void && t.destination_cycle_id === cycleId)
    .reduce((sum, t) => sum + (Number(t.head_count) || 0), 0);

  const mortality = mortalities
    .filter((m) => !m.is_void)
    .reduce((sum, m) => sum + (Number(m.head_count) || 0), 0);

  const transfersOutCount = transfersOut
    .filter((t) => !t.is_void && t.source_cycle_id === cycleId)
    .reduce((sum, t) => sum + (Number(t.head_count) || 0), 0);

  const pigsSold = sales
    .filter((s) => !s.is_void)
    .reduce((sum, s) => sum + (Number(s.head_count ?? s.pigs_sold_count) || 0), 0);

  const expectedCurrentHeadcount =
    pigsReceived + transfersInCount - mortality - transfersOutCount - pigsSold;

  return {
    pigsReceived,
    transfersIn: transfersInCount,
    mortality,
    transfersOut: transfersOutCount,
    pigsSold,
    expectedCurrentHeadcount,
  };
}

export const cycleService = {
  /**
   * Fetches cycles list with optional farm, status, and search filters.
   */
  async getCycles(filters: CycleFilters = {}): Promise<ProductionCycleRecord[]> {
    let query = supabase
      .from('production_cycles')
      .select(`
        id,
        cycle_code,
        batch_code,
        status,
        operation_type,
        start_date,
        expected_sale_date,
        actual_close_date,
        initial_head_count,
        current_head_count,
        notes,
        is_void,
        void_reason,
        created_at,
        updated_at,
        growers (id, full_name, grower_code),
        farms (id, farm_name),
        production_units (id, unit_code, unit_name),
        grower_contracts (id, contract_number, vsb_share_pct, grower_share_pct)
      `)
      .eq('is_void', false)
      .order('start_date', { ascending: false });

    if (filters.farmId) {
      query = query.eq('farm_id', filters.farmId);
    }
    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[cycleService.getCycles] Error fetching cycles:', error.message);
      throw error;
    }

    let results = (data || []) as unknown as ProductionCycleRecord[];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      results = results.filter((c) => {
        const code = c.cycle_code?.toLowerCase() || '';
        const grower = c.growers?.full_name?.toLowerCase() || '';
        const farm = c.farms?.farm_name?.toLowerCase() || '';
        return code.includes(term) || grower.includes(term) || farm.includes(term);
      });
    }

    if (results.length === 0) {
      return [];
    }

    const cycleIds = results.map((c) => c.id);

    // Batch query physical activity and reconciliation issues for reactive headcount
    const chunkSize = 200;
    const chunkedIds: string[][] = [];
    for (let i = 0; i < cycleIds.length; i += chunkSize) {
      chunkedIds.push(cycleIds.slice(i, i + chunkSize));
    }

    const arrivalsMap: Record<string, number> = {};
    const mortalitiesMap: Record<string, number> = {};
    const transfersInMap: Record<string, number> = {};
    const transfersOutMap: Record<string, number> = {};
    const salesMap: Record<string, number> = {};
    const issueStatusMap: Record<string, 'needs_review' | 'historical_incomplete_accepted'> = {};

    for (const chunk of chunkedIds) {
      try {
        const [arrivalsRes, mortalitiesRes, transfersInRes, transfersOutRes, salesRes, issuesRes] =
          await Promise.all([
            supabase
              .from('pig_arrivals')
              .select('cycle_id, head_count')
              .in('cycle_id', chunk)
              .eq('is_void', false),
            supabase
              .from('mortality_events')
              .select('cycle_id, head_count')
              .in('cycle_id', chunk)
              .eq('is_void', false),
            supabase
              .from('pig_transfers')
              .select('destination_cycle_id, head_count')
              .in('destination_cycle_id', chunk)
              .eq('is_void', false),
            supabase
              .from('pig_transfers')
              .select('source_cycle_id, head_count')
              .in('source_cycle_id', chunk)
              .eq('is_void', false),
            supabase
              .from('pig_sales')
              .select('cycle_id, head_count')
              .in('cycle_id', chunk)
              .eq('is_void', false),
            supabase
              .from('reconciliation_issues')
              .select('cycle_id, entity_id, status, issue_type')
              .or(`cycle_id.in.(${chunk.join(',')}),entity_id.in.(${chunk.join(',')})`),
          ]);

        (arrivalsRes.data || []).forEach((row: any) => {
          arrivalsMap[row.cycle_id] = (arrivalsMap[row.cycle_id] || 0) + (Number(row.head_count) || 0);
        });

        (mortalitiesRes.data || []).forEach((row: any) => {
          mortalitiesMap[row.cycle_id] = (mortalitiesMap[row.cycle_id] || 0) + (Number(row.head_count) || 0);
        });

        (transfersInRes.data || []).forEach((row: any) => {
          if (row.destination_cycle_id) {
            transfersInMap[row.destination_cycle_id] =
              (transfersInMap[row.destination_cycle_id] || 0) + (Number(row.head_count) || 0);
          }
        });

        (transfersOutRes.data || []).forEach((row: any) => {
          if (row.source_cycle_id) {
            transfersOutMap[row.source_cycle_id] =
              (transfersOutMap[row.source_cycle_id] || 0) + (Number(row.head_count) || 0);
          }
        });

        (salesRes.data || []).forEach((row: any) => {
          salesMap[row.cycle_id] = (salesMap[row.cycle_id] || 0) + (Number(row.head_count) || 0);
        });

        (issuesRes.data || []).forEach((row: any) => {
          const targetCycleId = row.cycle_id || row.entity_id;
          if (!targetCycleId) return;

          if (row.status === 'needs_review') {
            issueStatusMap[targetCycleId] = 'needs_review';
          } else if (
            row.status === 'historical_incomplete_accepted' &&
            issueStatusMap[targetCycleId] !== 'needs_review'
          ) {
            issueStatusMap[targetCycleId] = 'historical_incomplete_accepted';
          }
        });
      } catch (err: any) {
        console.warn('[cycleService.getCycles] Warning aggregating physical activities:', err.message);
      }
    }

    results.forEach((c) => {
      const received = arrivalsMap[c.id] || 0;
      const tIn = transfersInMap[c.id] || 0;
      const mort = mortalitiesMap[c.id] || 0;
      const tOut = transfersOutMap[c.id] || 0;
      const sold = salesMap[c.id] || 0;
      const liveHead = received + tIn - mort - tOut - sold;

      c.headcount = {
        pigsReceived: received,
        transfersIn: tIn,
        mortality: mort,
        transfersOut: tOut,
        pigsSold: sold,
        expectedCurrentHeadcount: liveHead,
      };

      c.reconciliation_status = issueStatusMap[c.id] || null;
    });

    return results;
  },

  /**
   * Fetches a single cycle detail including all related activities.
   */
  async getCycleDetail(cycleId: string): Promise<{
    cycle: ProductionCycleRecord;
    headcount: ReactiveHeadcountSummary;
    arrivals: PigArrivalRecord[];
    mortalities: MortalityEventRecord[];
    transfersIn: PigTransferRecord[];
    transfersOut: PigTransferRecord[];
    sales: any[];
  }> {
    const { data: cycle, error: cycleErr } = await supabase
      .from('production_cycles')
      .select(`
        id,
        cycle_code,
        batch_code,
        status,
        operation_type,
        start_date,
        expected_sale_date,
        actual_close_date,
        notes,
        legacy_raiser_name,
        legacy_batch_code,
        is_void,
        void_reason,
        created_at,
        updated_at,
        growers (id, full_name, grower_code, contact_number, address),
        farms (id, farm_name, barangay, municipality_city),
        production_units (id, unit_code, unit_name, capacity_head),
        grower_contracts (id, contract_number, vsb_share_pct, grower_share_pct, start_date, end_date)
      `)
      .eq('id', cycleId)
      .single();

    if (cycleErr || !cycle) {
      throw new Error(`Cycle not found: ${cycleErr?.message || cycleId}`);
    }

    // Fetch non-void activity records
    const [arrivalsRes, mortalitiesRes, transfersInRes, transfersOutRes, salesRes] = await Promise.all([
      supabase
        .from('pig_arrivals')
        .select(`
          *,
          suppliers (id, supplier_name),
          pig_arrival_items (*)
        `)
        .eq('cycle_id', cycleId)
        .eq('is_void', false)
        .order('arrival_date', { ascending: true }),

      supabase
        .from('mortality_events')
        .select(`
          *,
          production_units (id, unit_code, unit_name)
        `)
        .eq('cycle_id', cycleId)
        .eq('is_void', false)
        .order('event_date', { ascending: true }),

      supabase
        .from('pig_transfers')
        .select(`
          *,
          source_cycle:production_cycles!pig_transfers_source_cycle_id_fkey (id, cycle_code),
          destination_cycle:production_cycles!pig_transfers_destination_cycle_id_fkey (id, cycle_code),
          source_unit:production_units!pig_transfers_source_unit_id_fkey (id, unit_code, unit_name),
          destination_unit:production_units!pig_transfers_destination_unit_id_fkey (id, unit_code, unit_name)
        `)
        .eq('destination_cycle_id', cycleId)
        .eq('is_void', false)
        .order('transfer_date', { ascending: true }),

      supabase
        .from('pig_transfers')
        .select(`
          *,
          source_cycle:production_cycles!pig_transfers_source_cycle_id_fkey (id, cycle_code),
          destination_cycle:production_cycles!pig_transfers_destination_cycle_id_fkey (id, cycle_code),
          source_unit:production_units!pig_transfers_source_unit_id_fkey (id, unit_code, unit_name),
          destination_unit:production_units!pig_transfers_destination_unit_id_fkey (id, unit_code, unit_name)
        `)
        .eq('source_cycle_id', cycleId)
        .eq('is_void', false)
        .order('transfer_date', { ascending: true }),

      supabase
        .from('pig_sales')
        .select(`
          *,
          buyers (id, buyer_name, buyer_code, contact_person, contact_number, address),
          transaction_categories (id, code, name),
          rate_rules (id, rule_name, rule_code, rate_value, rate_unit),
          pig_sale_items (*)
        `)
        .eq('cycle_id', cycleId)
        .eq('is_void', false)
        .order('sale_date', { ascending: true }),
    ]);

    const arrivals = (arrivalsRes.data || []) as PigArrivalRecord[];
    const mortalities = (mortalitiesRes.data || []) as MortalityEventRecord[];
    const transfersIn = (transfersInRes.data || []) as PigTransferRecord[];
    const transfersOut = (transfersOutRes.data || []) as PigTransferRecord[];
    const sales = (salesRes.data || []) as any[];

    const headcount = computeReactiveHeadcount(
      cycleId,
      arrivals,
      transfersIn,
      mortalities,
      transfersOut,
      sales
    );

    return {
      cycle: cycle as unknown as ProductionCycleRecord,
      headcount,
      arrivals,
      mortalities,
      transfersIn,
      transfersOut,
      sales,
    };
  },

  /**
   * Creates a new production cycle.
   * Validates that contract_grower has a valid contract_id with explicit split.
   * Allows company_owned cycles to have null grower_id and null contract_id.
   */
  async createCycle(payload: CreateCyclePayload): Promise<ProductionCycleRecord> {
    if (payload.operation_type === 'contract_grower') {
      if (!payload.grower_id) {
        throw new Error('Grower is required for Contract Growing production cycles.');
      }
      if (!payload.contract_id) {
        throw new Error(
          'An active Grower Contract with defined profit sharing percentages is required for Contract Growing.'
        );
      }
    }

    if (!payload.cycle_code || !payload.cycle_code.trim()) {
      throw new Error('Cycle code is required.');
    }
    if (!payload.start_date) {
      throw new Error('Placement / Start date is required.');
    }

    const insertData: any = {
      cycle_code: payload.cycle_code.trim(),
      batch_code: payload.batch_code?.trim() || payload.cycle_code.trim(),
      operation_type: payload.operation_type,
      status: 'active',
      start_date: payload.start_date,
      expected_sale_date: payload.expected_sale_date || null,
      notes: payload.notes || null,
      farm_id: payload.farm_id || null,
      production_unit_id: payload.production_unit_id || null,
    };

    if (payload.operation_type === 'contract_grower') {
      insertData.grower_id = payload.grower_id;
      insertData.contract_id = payload.contract_id;
    } else {
      insertData.grower_id = payload.grower_id || null;
      insertData.contract_id = payload.contract_id || null;
    }

    const { data, error } = await supabase
      .from('production_cycles')
      .insert(insertData)
      .select(`
        id,
        cycle_code,
        batch_code,
        status,
        operation_type,
        start_date,
        expected_sale_date,
        notes,
        growers (id, full_name),
        farms (id, farm_name)
      `)
      .single();

    if (error) {
      console.error('[cycleService.createCycle] Error creating cycle:', error.message);
      throw error;
    }

    return data as unknown as ProductionCycleRecord;
  },

  /**
   * Updates lifecycle status of a production cycle.
   */
  async updateCycleStatus(
    cycleId: string,
    status: CycleStatus,
    actualCloseDate?: string | null
  ): Promise<void> {
    const updatePayload: any = {
      status,
      updated_at: new Date().toISOString(),
    };

    if (status === 'closed' && actualCloseDate) {
      updatePayload.actual_close_date = actualCloseDate;
    }

    const { error } = await supabase
      .from('production_cycles')
      .update(updatePayload)
      .eq('id', cycleId);

    if (error) {
      throw new Error(`Failed to update cycle status: ${error.message}`);
    }
  },

  /**
   * Fetches active growers with their active contracts.
   */
  async getGrowersWithActiveContracts(): Promise<
    Array<GrowerRecord & { contracts: GrowerContractRecord[] }>
  > {
    const [growersRes, contractsRes] = await Promise.all([
      supabase
        .from('growers')
        .select('id, grower_code, full_name, contact_number, farm_location_id, address, is_active')
        .eq('is_void', false)
        .eq('is_active', true)
        .order('full_name', { ascending: true }),

      supabase
        .from('grower_contracts')
        .select('id, contract_number, grower_id, farm_id, production_unit_id, start_date, end_date, vsb_share_pct, grower_share_pct, terms_and_conditions, is_active')
        .eq('is_void', false)
        .eq('is_active', true),
    ]);

    if (growersRes.error) {
      console.error('[cycleService.getGrowers] Error:', growersRes.error.message);
      return [];
    }

    const contractsByGrower: Record<string, GrowerContractRecord[]> = {};
    (contractsRes.data || []).forEach((c) => {
      if (!contractsByGrower[c.grower_id]) {
        contractsByGrower[c.grower_id] = [];
      }
      contractsByGrower[c.grower_id].push(c);
    });

    return (growersRes.data || []).map((g) => ({
      ...g,
      contracts: contractsByGrower[g.id] || [],
    }));
  },

  /**
   * Fetches production units for a farm or grower.
   */
  async getProductionUnits(farmId?: string | null, growerId?: string | null): Promise<ProductionUnitRecord[]> {
    let query = supabase
      .from('production_units')
      .select('id, farm_id, grower_id, unit_code, unit_name, capacity_head, is_active')
      .eq('is_void', false)
      .eq('is_active', true)
      .order('unit_name', { ascending: true });

    if (farmId) {
      query = query.eq('farm_id', farmId);
    }
    if (growerId) {
      query = query.eq('grower_id', growerId);
    }

    const { data, error } = await query;
    if (error) {
      console.warn('[cycleService.getProductionUnits] Warn:', error.message);
      return [];
    }
    return (data || []) as ProductionUnitRecord[];
  },

  /**
   * Standardized soft-void updater for any operational table.
   */
  async softVoidRecord(tableName: string, recordId: string, voidReason: string): Promise<void> {
    if (!voidReason || !voidReason.trim()) {
      throw new Error('Mandatory void reason is required.');
    }

    const { error } = await supabase
      .from(tableName)
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', recordId);

    if (error) {
      throw new Error(`Failed to void record in ${tableName}: ${error.message}`);
    }
  },
};
